import React, { useState, useEffect } from 'react';
import Styles from './styles.module.css';
import { getFirestore, query, collection, where, getDocs } from 'firebase/firestore';
import Title from '../title';
import AppModal from '../app-modal/index';
import { IoPeopleOutline, IoLocationOutline, IoTimeOutline, IoCopyOutline } from 'react-icons/io5';

type InfoSession = {
  id: string;
  name: string;
  description: string;
  startAt: Date | null;
  endAt: Date | null;
  closed: boolean;
  durationMin: number;
  place: string;
  people: number;
};

type RecruitInfoSessionsProps = {
  companyId: string;
};

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];
const hm = (dt: Date) => `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;

const RecruitInfoSessions: React.FC<RecruitInfoSessionsProps> = ({ companyId }) => {

  const [ sessions, setSessions ] = useState<InfoSession[]>([]);
  const [ showModal, setShowModal ] = useState(false);

  useEffect(() => {
    if (!companyId) return;

    const fetchSessions = async () => {
      const db = getFirestore();
      const q = query(
        collection(db, 'infoSessions'),
        where('companyId', '==', companyId)
      );
      const snap = await getDocs(q);
      const tmp: InfoSession[] = snap.docs.map(d => ({
        id: d.id,
        name: d.data().name ?? '',
        description: d.data().description ?? '',
        startAt: d.data().schedule?.startAt?.toDate ? d.data().schedule.startAt.toDate() : null,
        endAt: d.data().schedule?.endAt?.toDate ? d.data().schedule.endAt.toDate() : null,
        closed: d.data().closed === true,
        durationMin: d.data().schedule?.durationMin ?? 0,
        place: d.data().place ?? '',
        people: d.data().people ?? 0,
      }));
      tmp.sort((a, b) => (a.startAt?.getTime() ?? 0) - (b.startAt?.getTime() ?? 0));
      // 終わった説明会は出さない(学生アプリと同じ)
      const now = Date.now();
      setSessions(tmp.filter(t => !t.startAt || t.startAt.getTime() > now));
    };

    fetchSessions();
  }, [companyId]);

  if (sessions.length === 0) return null;

  const copy = (text: string) => { navigator.clipboard?.writeText(text).catch(() => {}); };

  return (
    <>
      <div className={Styles.hr} />
      <Title title="会社説明会" desc="サロンが開催する会社説明会の日程をご確認ください" />

      <div className={Styles.list}>
        {sessions.map(session => {
          const start = session.startAt;
          const time = !start ? '日時未定'
            : session.endAt ? `${hm(start)}〜${hm(session.endAt)}` : `${hm(start)}〜`;
          return (
            <div key={session.id} className={Styles.card}>
              <div className={Styles.card_top}>
                <div className={Styles.date_tile}>
                  <b>{start ? `${start.getMonth() + 1}/${start.getDate()}` : '未定'}</b>
                  {start && <span>{WEEKDAYS[start.getDay()]}</span>}
                </div>
                <div className={Styles.card_main}>
                  <h3 className={Styles.card_name}>{session.name}</h3>
                  <div className={Styles.chips}>
                    <span className={Styles.chip}><IoTimeOutline />{session.durationMin > 0 ? `${time}（${session.durationMin}分）` : time}</span>
                    {session.people > 0 && <span className={Styles.chip}><IoPeopleOutline />定員 {session.people}名</span>}
                  </div>
                </div>
              </div>

              {session.place && (
                <button type="button" className={`${Styles.chip} ${Styles.chip_place}`} onClick={() => copy(session.place)}>
                  <IoLocationOutline />{session.place}<IoCopyOutline className={Styles.chip_copy} />
                </button>
              )}

              {session.description && (
                <p className={Styles.card_desc}>{session.description}</p>
              )}

              <button
                type="button"
                className={session.closed ? `${Styles.card_btn} ${Styles.card_btn_off}` : Styles.card_btn}
                disabled={session.closed}
                onClick={() => setShowModal(true)}>
                {session.closed ? '受付終了' : '説明会に参加する'}
              </button>
            </div>
          );
        })}
      </div>

      {showModal && <AppModal onClose={() => setShowModal(false)} />}
    </>
  );
};

export default RecruitInfoSessions;
